import { COUNTRIES, STATES_AND_TERRITORIES } from '@/constants/state';
import { SVPlacesAutocomplete, SVTextInput, SVAutocomplete } from './core';
import { useFormContext } from 'react-hook-form';
import { Address } from '@/types/common';
import { Grid } from '@mui/material';
import { FORM_FIELDS } from '@/constants/formFields';
import { useEffect } from 'react';

type AddressFormFieldsProps = {
  line1Name: string;
  line2Name: string;
  cityName: string;
  stateName: string;
  postalCodeName: string;
  isOptional?: boolean;
} & (
  | {
      isInternational: true;
      countryName: string;
    }
  | {
      isInternational?: false;
      countryName?: string;
    }
);

export default function AddressFormFields({
  line1Name,
  line2Name,
  cityName,
  stateName,
  postalCodeName,
  isInternational,
  countryName,
  isOptional,
}: AddressFormFieldsProps) {
  const methods = useFormContext();
  const { watch, setValue, clearErrors } = methods;

  function handleSelectCurrentAddress(address: Address) {
    setValue(line1Name, address.line1);
    setValue(cityName, address.city);
    setValue(stateName, address.state);
    setValue(postalCodeName, address.postalCode);
  }
  const [city, state, postalCode] = watch([
    cityName,
    stateName,
    postalCodeName,
  ]);
  useEffect(() => {
    clearErrors(cityName);
  }, [city]);
  useEffect(() => {
    clearErrors(stateName);
  }, [state]);
  useEffect(() => {
    clearErrors(postalCodeName);
  }, [postalCode]);

  useEffect(() => {
    if (countryName) {
      setValue(countryName, isInternational ? '' : 'US');
    }
  }, [isInternational]);
  const required = isOptional ? false : true;

  return (
    <Grid container spacing={2}>
      <Grid size={{ xs: 12 }}>
        <SVPlacesAutocomplete
          name={line1Name}
          handleSelectPlace={handleSelectCurrentAddress}
          allowInternational={isInternational}
          {...FORM_FIELDS.LINE1}
          rules={{ required }}
        />
      </Grid>
      <Grid size={{ xs: 12 }}>
        <SVTextInput name={line2Name} {...FORM_FIELDS.LINE2} />
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <SVTextInput
          name={cityName}
          {...FORM_FIELDS.CITY}
          rules={{ required }}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        {isInternational ? (
          <SVTextInput
            name={stateName}
            valueFormatter={value => value.toUpperCase()}
            {...FORM_FIELDS.PROVINCE}
            rules={{ required }}
          />
        ) : (
          <SVAutocomplete
            name={stateName}
            {...FORM_FIELDS.STATE}
            options={STATES_AND_TERRITORIES}
            rules={{ required }}
          />
        )}
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <SVTextInput
          name={postalCodeName}
          {...FORM_FIELDS.ZIP}
          rules={{ required }}
        />
      </Grid>
      {isInternational ? (
        <Grid size={{ xs: 12, md: 4 }}>
          <SVAutocomplete
            name={countryName}
            {...FORM_FIELDS.COUNTRY}
            options={COUNTRIES}
            rules={{ required }}
          />
        </Grid>
      ) : null}
    </Grid>
  );
}
